import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import {
	AlertTriangle,
	ArrowUpRight,
	Check,
	Info,
	Lock,
	Share2,
	ShieldCheck,
	Sparkles,
	Users,
} from "lucide-react";
import { lazy, Suspense, useState } from "react";
import { PromoRecommendation } from "@/components/AdvertiseCta";
import { AgentCard } from "@/components/AgentCard";
import { AgentInsights } from "@/components/AgentInsights";
import { AgentLogoImage } from "@/components/agents/AgentLogoImage";
import { VerifiedBadge } from "@/components/agents/VerifiedBadge";
import { useClaimWebsiteDialog } from "@/components/ClaimWebsiteDialog";
import { CtaBlock } from "@/components/CtaBlock";
import { JsonLd } from "@/components/JsonLd";
import { NotFoundView } from "@/components/NotFoundView";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { ProviderLogo, providerDisplayName } from "@/components/ProviderLogo";
import { PublicPageLayout } from "@/components/PublicPageLayout";
import { PublicFooter } from "@/components/public-layout";
import { SignupForm } from "@/components/SignupForm";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSession } from "@/hooks/auth-hooks";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useFileUrl } from "@/hooks/use-file-url";
import {
	CATEGORY_ICONS,
	coerceAgentCategories,
	getAgentCategoryLabel,
} from "@/lib/agent-categories";
import { agentProfileQueryOptions } from "@/lib/agent-profile.query";
import { trackEvent } from "@/lib/analytics";
import { currencyAtom } from "@/lib/currency";
import {
	formatCurrency as formatCurrencyUtil,
	formatPercent as formatPercentUtil,
} from "@/lib/format";
import { homepageLeaderboardQueryOptions } from "@/lib/leaderboard.query";
import { buildOutboundUrl } from "@/lib/outbound-url";
import {
	comparePublicAgentRanking,
	type PublicAgentView,
} from "@/lib/public-agents";
import { breadcrumbListSchema, profilePageSchema } from "@/lib/schema";
import {
	buildLocalizedSeoLinks,
	dynamicAgentOgUrl,
	getLocalizedSeoUrls,
} from "@/lib/seo";
import { getStaleRevenueSync } from "@/lib/stripe-sync-status";

type AgentRecord = PublicAgentView & {
	revenueSnapshots?: SnapshotRecord[];
};

type SnapshotRecord = {
	activeSubscriptions?: number | null;
	chargeCount?: number;
	churnRate?: number;
	createdAt: Date | string;
	currency?: string;
	estimatedMrr: number;
	grossRevenue: number;
	id: string;
	snapshotDate: Date | string;
	source?: string;
};

type ChartWindow = "24h" | "7d" | "30d" | "3m" | "6m" | "12m" | "all";

type ChartMetric = "revenue" | "churn";

const AgentRevenueChart = lazy(() =>
	import("@/components/AgentRevenueChart").then((module) => ({
		default: module.AgentRevenueChart,
	})),
);

const CHART_WINDOWS: Array<{ label: string; value: ChartWindow }> = [
	{ label: "24 hours", value: "24h" },
	{ label: "Last 7 days", value: "7d" },
	{ label: "Last 30 days", value: "30d" },
	{ label: "Last 3 months", value: "3m" },
	{ label: "Last 6 months", value: "6m" },
	{ label: "Last 12 months", value: "12m" },
	{ label: "All time", value: "all" },
];

// PostHog flag gating the Churn chart metric. Off until enabled in PostHog.
const SHOW_CHURN_FLAG = "show-churn";

const METRIC_OPTIONS: Array<{ label: string; value: ChartMetric }> = [
	{ label: "Revenue", value: "revenue" },
	{ label: "Churn", value: "churn" },
];

const WINDOW_DAY_COUNT: Record<Exclude<ChartWindow, "all">, number> = {
	"24h": 2,
	"7d": 7,
	"30d": 30,
	"3m": 90,
	"6m": 180,
	"12m": 365,
};

export const Route = createFileRoute("/agents/$agentSlug")({
	component: AgentDetailPage,
	loader: ({ context, params }) =>
		Promise.all([
			context.queryClient.ensureQueryData(
				agentProfileQueryOptions(params.agentSlug),
			),
			context.queryClient.ensureQueryData(homepageLeaderboardQueryOptions),
		]),
	head: ({ params, matches, loaderData }) => {
		const name = params.agentSlug
			.split("-")
			.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
			.join(" ");
		const internalPath = `/agents/${params.agentSlug}`;
		const { canonicalUrl } = getLocalizedSeoUrls({ matches, internalPath });
		const desc = `${name}: provider-verified MRR, active subscriptions, revenue history, and leaderboard ranking on AgentMRR - the public revenue leaderboard for AI agents.`;
		const agent = loaderData?.[0]?.agent;
		const ogImage = dynamicAgentOgUrl(params.agentSlug, agent?.estimatedMrr);
		return {
			meta: [
				{ title: `${name} - AgentMRR` },
				{ name: "description", content: desc },
				{ property: "og:title", content: `${name} - AgentMRR` },
				{ property: "og:description", content: desc },
				{ property: "og:image", content: ogImage },
				{ property: "og:image:width", content: "1200" },
				{ property: "og:image:height", content: "630" },
				{ property: "og:url", content: canonicalUrl },
				{ property: "og:type", content: "profile" },
				{ property: "og:site_name", content: "AgentMRR" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:site", content: "@agentmrr" },
				{ name: "twitter:title", content: `${name} - AgentMRR` },
				{ name: "twitter:description", content: desc },
				{ name: "twitter:image", content: ogImage },
				{ name: "twitter:image:alt", content: `${name} revenue card` },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath }),
		};
	},
});

function AgentDetailPage() {
	const { agentSlug } = Route.useParams();
	const churnEnabled = useFeatureFlag(SHOW_CHURN_FLAG);
	const [metric, setMetric] = useState<ChartMetric>("revenue");
	// Hide the Churn metric until the flag is on; fall back to Revenue if a
	// churn selection lingers when the flag resolves off.
	const metricOptions = churnEnabled
		? METRIC_OPTIONS
		: METRIC_OPTIONS.filter((option) => option.value !== "churn");
	const activeMetric = churnEnabled || metric !== "churn" ? metric : "revenue";
	const [range, setRange] = useState<ChartWindow>("all");
	const [shareCopied, setShareCopied] = useState(false);
	const [signInOpen, setSignInOpen] = useState(false);
	const currency = useAtomValue(currencyAtom);
	const { user } = useSession();
	const { openClaimWebsiteDialog } = useClaimWebsiteDialog();

	const { data: listingsData } = useQuery({
		enabled: !!user?.id,
		queryKey: ["agent-listings"],
		queryFn: async () => {
			const res = await fetch("/api/agents/listings", {
				headers: { Accept: "application/json" },
			});
			if (res.status === 401) return { agents: [] };
			if (!res.ok) throw new Error("Failed to fetch listings");
			return res.json() as Promise<{
				agents: Array<{ agentId: string; slug: string }>;
			}>;
		},
		retry: false,
	});

	const { data, isLoading } = useQuery(agentProfileQueryOptions(agentSlug));

	const { data: leaderboardData } = useQuery(homepageLeaderboardQueryOptions);

	const liveAgents = (leaderboardData?.agents ?? []) as AgentRecord[];
	const agent = (data?.agent ?? null) as AgentRecord | null;

	if (isLoading && !agent) {
		return <AgentDetailSkeleton />;
	}

	if (!agent) {
		return (
			<NotFoundView
				label="Agent"
				description="This slug doesn't have a public AgentMRR profile."
			/>
		);
	}

	if (typeof document !== "undefined") {
		document.title = `${agent.displayName} - ${formatCurrencyUtil(
			agent.estimatedMrr,
			currency,
		)} | AgentMRR`;
	}

	const snapshots = normalizeSnapshots(agent);
	const visiblePoints = filterChartPoints(snapshots, range);

	const previousPeriodPoints = (() => {
		if (visiblePoints.length < 2) return [];
		const startDate = new Date(visiblePoints[0].snapshotDate);
		const endDate = new Date(
			visiblePoints[visiblePoints.length - 1].snapshotDate,
		);
		const durationMs = endDate.getTime() - startDate.getTime();
		const prevEnd = new Date(startDate.getTime() - 1);
		const prevStart = new Date(prevEnd.getTime() - durationMs);
		return snapshots.filter((p) => {
			const t = new Date(p.snapshotDate).getTime();
			return t >= prevStart.getTime() && t <= prevEnd.getTime();
		});
	})();

	const latestPoint =
		visiblePoints[visiblePoints.length - 1] ?? snapshots[snapshots.length - 1];
	const previousPoint = visiblePoints.length > 1 ? visiblePoints[0] : null;
	const getMetricValue = (point: SnapshotRecord | null | undefined) => {
		if (!point) return null;
		if (activeMetric === "churn") return point.churnRate ?? null;
		return point.grossRevenue;
	};
	const periodEnd = getMetricValue(latestPoint) ?? 0;
	const periodStart = getMetricValue(previousPoint);
	// grossRevenue is stored cumulatively (the latest point equals all-time
	// revenue), so for a bounded window the headline shows revenue earned within
	// that window. Churn is point-in-time, so it always shows the latest value.
	const chartValue =
		activeMetric === "revenue" && range !== "all" && periodStart != null
			? Math.max(periodEnd - periodStart, 0)
			: periodEnd;
	const chartDelta =
		periodStart == null ? 0 : calculatePercentChange(periodStart, periodEnd);
	const discoverMoreAgents = buildDiscoverMoreAgents(liveAgents, agent);
	const staleRevenueSync = getStaleRevenueSync(agent);
	const isOwner =
		!!user &&
		(listingsData?.agents ?? []).some(
			(a) => a.slug === agentSlug || a.agentId === agent.id,
		);
	const agentRank = (() => {
		const sorted = liveAgents.toSorted(comparePublicAgentRanking);
		const idx = sorted.findIndex((e) => e.slug === agent.slug);
		return idx >= 0 ? idx + 1 : 0;
	})();

	const agentPath = `/agents/${agent.displayPathSegment}`;

	return (
		<PublicPageLayout>
			<JsonLd
				data={[
					breadcrumbListSchema([
						{ name: "Home", path: "/" },
						{ name: "Agents", path: "/" },
						{ name: agent.displayName, path: agentPath },
					]),
					profilePageSchema({
						name: agent.displayName,
						description: agent.displayDescription,
						path: agentPath,
						image: agent.logoUrl ?? undefined,
					}),
				]}
			/>
			<main className="mx-auto w-full max-w-5xl pt-4 pb-16 sm:pt-6">
				<PageBreadcrumb
					items={[{ label: "Agents", to: "/" }, { label: agent.displayName }]}
				/>
				{staleRevenueSync ? (
					<RevenueSyncAttentionBanner
						lastSyncedAt={staleRevenueSync.lastSyncedAt}
						provider={staleRevenueSync.provider}
					/>
				) : null}

				<section className="mt-10">
					<div className="flex items-center justify-between gap-3 pb-3 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
						{agent.verificationStatus === "verified" ? (
							<span className="inline-flex items-center gap-1.5 tracking-[0.12em]">
								<AgentLivePulse />
								<span className="text-foreground">Live</span>
							</span>
						) : null}
						{agentRank > 0 ? (
							<span className="ml-auto tabular-nums">
								#{String(agentRank)}
								<span className="ml-1 text-muted-foreground">on AgentMRR</span>
							</span>
						) : null}
					</div>

					<div className="flex flex-col gap-8 border-t border-border pt-3 @3xl/page:flex-row @3xl/page:items-start @3xl/page:justify-between">
						<div className="flex min-w-0 flex-1 items-start gap-5 @md/page:gap-7">
							<AgentLogoImage
								logoUrl={agent.logoUrl}
								website={agent.displayWebsite}
								mode={agent.displayAvatarMode}
								name={agent.displayInitials}
								size="lg"
							/>
							<div className="min-w-0 space-y-2.5 pt-1">
								<div className="flex items-center gap-2">
									<h1
										className="font-mono text-[clamp(1.375rem,2.2vw,1.875rem)] leading-[1.1] tracking-[-0.02em] text-foreground"
										style={{ fontWeight: 400 }}
									>
										{agent.displayName}
									</h1>
									{agent.verificationStatus === "verified" ? (
										<VerifiedBadge
											provider={agent.verificationProvider}
											size={20}
										/>
									) : null}
								</div>
								{agent.displayAvatarMode !== "stealth" &&
								coerceAgentCategories(agent.categories).length > 0 ? (
									<div className="flex flex-wrap gap-x-4 gap-y-2">
										{coerceAgentCategories(agent.categories).map((category) => (
											<CategoryLink key={category} category={category} />
										))}
									</div>
								) : null}
								<p className="max-w-2xl text-base leading-[1.6] text-muted-foreground">
									{agent.displayDescription}
								</p>
							</div>
						</div>

						<div className="flex w-fit shrink-0 flex-row gap-2">
							<button
								type="button"
								onClick={() => {
									navigator.clipboard.writeText(window.location.href);
									trackEvent("agent_shared", {
										agent_slug: agent.displayPathSegment,
									});
									setShareCopied(true);
									window.setTimeout(() => setShareCopied(false), 2000);
								}}
								className="inline-flex min-h-10 w-fit items-center justify-center gap-2 border border-border bg-background px-4 font-mono text-sm uppercase tracking-[0.14em] text-foreground transition-colors hover:border-brand/40 hover:text-brand"
							>
								{shareCopied ? (
									<>
										<Check className="size-3.5 text-brand" />
										Copied
									</>
								) : (
									<>
										<Share2 className="size-3.5" />
										Share
									</>
								)}
							</button>
							<button
								type="button"
								onClick={() => {
									trackEvent("agent_claim_clicked", {
										agent_slug: agent.displayPathSegment,
										verification_status: agent.websiteVerificationStatus,
									});
									openClaimWebsiteDialog({
										agentSlug: agent.displayPathSegment,
										agentName: agent.displayName,
										website: agent.displayWebsite,
										currentStatus: agent.websiteVerificationStatus,
									});
								}}
								className="inline-flex min-h-10 w-fit items-center justify-center gap-2 border border-border bg-background px-4 font-mono text-sm uppercase tracking-[0.14em] text-foreground transition-colors hover:border-brand/40 hover:text-brand"
							>
								<ShieldCheck className="size-3.5" />
								{agent.websiteVerificationStatus === "verified"
									? "Embed"
									: "Claim"}
							</button>
							{agent.displayWebsite ? (
								<a
									href={buildOutboundUrl(agent.displayWebsite, "agent-page")}
									target="_blank"
									rel="noreferrer"
									onClick={() =>
										trackEvent("agent_visited", {
											agent_slug: agent.displayPathSegment,
										})
									}
									className="btn-press inline-flex min-h-10 w-fit items-center justify-center gap-2 bg-brand px-5 font-mono text-sm uppercase tracking-[0.14em] text-background hover:bg-brand-hover"
								>
									Visit
									<ArrowUpRight className="size-3.5" />
								</a>
							) : null}
						</div>
					</div>
				</section>

				<section className="mt-12">
					<div className="grid grid-cols-2 gap-px border-y border-border bg-border @2xl/page:grid-cols-4">
						<TickerCell
							label="All-time"
							value={
								agent.isUnclaimed
									? "-"
									: formatCurrencyUtil(agent.allTimeRevenue, currency)
							}
							sub={
								agent.isUnclaimed
									? undefined
									: agentRank > 0
										? `Rank #${String(agentRank)}`
										: "Gross revenue"
							}
						/>
						<TickerCell
							label="MRR"
							labelSuffix={
								<Tooltip>
									<TooltipTrigger asChild>
										<Info className="size-3 cursor-help text-muted-foreground" />
									</TooltipTrigger>
									<TooltipContent className="max-w-64">
										MRR is an estimated value calculated from active
										subscriptions. It may differ from the payment provider's
										actual MRR due to prorations, cancellations, trials, and
										billing cycles.
									</TooltipContent>
								</Tooltip>
							}
							value={
								agent.isUnclaimed
									? "-"
									: formatCurrencyUtil(agent.estimatedMrr, currency)
							}
							sub={
								agent.isUnclaimed
									? undefined
									: `${new Intl.NumberFormat("en-US").format(agent.currentActiveSubscriptions)} subscriptions`
							}
						/>
						<TickerCell
							label="Founder"
							value={<FounderTickerValue agent={agent} />}
						/>
						<TickerCell
							label="Founded"
							value={
								<span className="uppercase">
									{formatFounded(agent.foundedAt)}
								</span>
							}
							sub={
								agent.country ? (
									<FoundedRegion country={agent.country} />
								) : undefined
							}
						/>
					</div>
				</section>

				<section className="mt-14">
					<div className="flex flex-col gap-4 pb-6 @2xl/page:flex-row @2xl/page:items-end @2xl/page:justify-between">
						<div>
							<div className="flex flex-wrap items-baseline gap-3">
								<p
									className="font-mono text-[clamp(1.5rem,3vw,2.25rem)] leading-none tracking-[-0.02em] text-foreground tabular-nums"
									style={{ fontWeight: 400 }}
								>
									{activeMetric === "churn"
										? formatPercentUtil(chartValue)
										: formatCurrencyUtil(chartValue, currency)}
								</p>
								<InlineChange value={chartDelta} />
							</div>
							<ProfitMarginStat
								value={agent.isUnclaimed ? null : agent.profitMarginPct}
								isAuthenticated={!!user}
								onUnlock={() => {
									trackEvent("profit_margin_unlock_clicked", {
										agent_slug: agent.displayPathSegment,
									});
									setSignInOpen(true);
								}}
							/>
						</div>

						<div className="flex items-center gap-2">
							<DetailSelect
								value={activeMetric}
								options={metricOptions}
								onChange={(value) => setMetric(value as ChartMetric)}
							/>
							<DetailSelect
								value={range}
								options={CHART_WINDOWS}
								onChange={(value) => setRange(value as ChartWindow)}
							/>
						</div>
					</div>

					<div className="border-t border-border pt-6">
						<Suspense fallback={<ChartPanelSkeleton />}>
							<AgentRevenueChart
								metric={activeMetric}
								points={visiblePoints}
								previousPoints={previousPeriodPoints}
							/>
						</Suspense>
					</div>
					{agent.verificationStatus === "verified" ? (
						<div className="mt-6 flex flex-wrap items-center gap-x-1.5 gap-y-1 font-mono text-sm text-muted-foreground">
							<VerifiedBadge provider={agent.verificationProvider} />
							{agent.verificationProvider ? (
								<span className="inline-flex flex-wrap items-center gap-x-1.5">
									Revenue is verified with a
									<ProviderLogo
										provider={agent.verificationProvider}
										className="inline size-4 align-text-bottom text-foreground"
									/>
									<span className="text-foreground">
										{providerDisplayName(agent.verificationProvider)}
									</span>
									API key.
								</span>
							) : (
								<span>Revenue is verified with an API key.</span>
							)}
							{agent.lastSyncedAt ? (
								<span>
									Last updated: {formatVerifiedTimestamp(agent.lastSyncedAt)}
								</span>
							) : null}
						</div>
					) : null}
				</section>

				<AgentInsights agentId={agent.id} agentInsights={agent.agentInsights} />

				<FounderStory agent={agent} />

				<PromoRecommendation seed={agent.slug} />

				{isOwner && (
					<div className="mt-12 flex justify-start">
						<Link
							to="/dashboard"
							className="inline-flex items-center gap-2 border border-border bg-background px-4 py-2 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:border-brand/40 hover:text-brand"
						>
							<Users className="size-3.5" />
							Manage in dashboard
						</Link>
					</div>
				)}

				{discoverMoreAgents.length > 0 && (
					<section className="mt-16">
						<div className="pb-6">
							<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
								&gt; Discover more agents
							</span>
						</div>
						<div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
							{discoverMoreAgents.map((entry) => (
								<AgentCard key={entry.id} agent={entry} />
							))}
						</div>
					</section>
				)}
				<CtaBlock />
				<div className="mt-16">
					<PublicFooter />
				</div>
			</main>

			<Dialog open={signInOpen} onOpenChange={setSignInOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Sign in to see profit margins</DialogTitle>
						<DialogDescription>
							Profit margins are visible to signed-in members. Create a free
							account or sign in to unlock them across every agent.
						</DialogDescription>
					</DialogHeader>
					<SignupForm
						postAuthUrl={agentPath}
						onSignedIn={() => setSignInOpen(false)}
						autoFocus
					/>
				</DialogContent>
			</Dialog>
		</PublicPageLayout>
	);
}

function AgentDetailSkeleton() {
	return (
		<div className="min-h-svh bg-background px-4 py-16 sm:px-6">
			<div className="mx-auto max-w-5xl animate-pulse space-y-6">
				<div className="flex items-center justify-between pb-4">
					<div className="h-3 w-40 bg-muted" />
					<div className="h-3 w-24 bg-muted" />
				</div>
				<div className="flex items-start gap-5 border-t border-border pt-6">
					<div className="size-20 border border-border bg-muted" />
					<div className="flex-1 space-y-3">
						<div className="h-7 w-48 bg-muted" />
						<div className="h-3 w-32 bg-muted" />
						<div className="h-3 w-full max-w-md bg-muted" />
					</div>
				</div>
				<div className="grid grid-cols-2 gap-px border-y border-border bg-border sm:grid-cols-4">
					{Array.from({ length: 4 }, (_, index) => (
						<div
							key={`skeleton-ticker-${index + 1}`}
							className="flex flex-col gap-2 bg-background px-4 py-3 sm:px-5 sm:py-4"
						>
							<div className="h-2.5 w-16 bg-muted" />
							<div className="h-6 w-20 bg-muted" />
							<div className="h-2.5 w-12 bg-muted" />
						</div>
					))}
				</div>
				<div className="h-[24rem] border border-border bg-muted/20" />
			</div>
		</div>
	);
}

function ProfitMarginStat({
	value,
	isAuthenticated,
	onUnlock,
}: {
	value: number | null | undefined;
	isAuthenticated: boolean;
	onUnlock: () => void;
}) {
	if (value === null || value === undefined) return null;
	const formatted = `${new Intl.NumberFormat("en-US", {
		maximumFractionDigits: 1,
	}).format(value)}%`;
	return (
		<div className="mt-2 flex items-center gap-2 font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
			<span>Profit margin</span>
			{isAuthenticated ? (
				<span className="text-foreground tabular-nums">{formatted}</span>
			) : (
				<button
					type="button"
					onClick={onUnlock}
					aria-label="Sign in to see profit margin"
					className="group inline-flex items-center gap-2"
				>
					<span
						aria-hidden="true"
						className="select-none blur-[5px] tabular-nums text-foreground"
					>
						{formatted}
					</span>
					<span className="inline-flex items-center gap-1 text-brand transition-colors group-hover:text-brand-hover">
						<Lock className="size-3" />
						Sign in
					</span>
				</button>
			)}
		</div>
	);
}

function TickerCell({
	label,
	labelSuffix,
	value,
	sub,
}: {
	label: string;
	labelSuffix?: React.ReactNode;
	value: React.ReactNode;
	sub?: React.ReactNode;
}) {
	return (
		<div className="flex flex-col gap-1.5 bg-background px-4 py-5 @2xl/page:px-6 @2xl/page:py-6 @2xl/page:first:pl-0">
			<span className="flex items-center gap-1.5 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
				{label}
				{labelSuffix}
			</span>
			<div
				className="min-w-0 font-mono text-[clamp(1.125rem,2vw,1.5rem)] leading-none text-foreground tabular-nums"
				style={{ fontWeight: 400 }}
			>
				{value}
			</div>
			<span className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
				{sub || "\u00A0"}
			</span>
		</div>
	);
}

function FounderTickerValue({ agent }: { agent: PublicAgentView }) {
	if (agent.isUnclaimed) {
		return "Unclaimed";
	}

	const avatar = (
		<FounderAvatarCompact
			avatarUrl={agent.displayFounderAvatarUrl}
			founderName={agent.displayFounderName}
		/>
	);
	const name = (
		<span className="min-w-0 break-words text-base leading-tight">
			{agent.displayFounderName}
		</span>
	);

	if (!agent.displayFounderSlug) {
		return (
			<span className="flex items-center gap-2">
				{avatar}
				{name}
			</span>
		);
	}

	return (
		<Link
			to="/founders/$founderSlug"
			params={{ founderSlug: agent.displayFounderSlug }}
			className="flex items-center gap-2 transition-opacity hover:opacity-80"
		>
			{avatar}
			{name}
		</Link>
	);
}

function FounderAvatarCompact({
	avatarUrl,
	founderName,
	size = "sm",
}: {
	avatarUrl: string | null;
	founderName: string;
	size?: "sm" | "lg";
}) {
	const { url: resolvedAvatarUrl } = useFileUrl(avatarUrl);
	const [failedAvatarUrl, setFailedAvatarUrl] = useState<string | null>(null);
	const sizeClass = size === "lg" ? "size-14" : "size-8";

	if (resolvedAvatarUrl && failedAvatarUrl !== resolvedAvatarUrl) {
		return (
			<img
				src={resolvedAvatarUrl}
				alt={founderName}
				className={`${sizeClass} shrink-0 border border-border bg-muted object-cover`}
				onError={() => setFailedAvatarUrl(resolvedAvatarUrl)}
				referrerPolicy="no-referrer"
			/>
		);
	}
	return (
		<span
			className={`${sizeClass} inline-flex shrink-0 items-center justify-center border border-border bg-muted text-sm text-foreground`}
			style={{ fontWeight: 400 }}
		>
			{founderName
				.split(/\s+/)
				.filter(Boolean)
				.slice(0, 2)
				.map((part) => part[0]?.toUpperCase())
				.join("")}
		</span>
	);
}

function CategoryLink({ category }: { category: string }) {
	const Icon = CATEGORY_ICONS[category] ?? Sparkles;
	return (
		<Link
			to="/categories/$category"
			params={{ category }}
			className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-brand"
		>
			<Icon className="size-3.5" aria-hidden="true" />
			{getAgentCategoryLabel(category)}
		</Link>
	);
}

function FoundedRegion({ country }: { country: string }) {
	const flag = countryToFlag(country);
	return (
		<span className="inline-flex items-center gap-1.5">
			{flag ? <span className="text-sm leading-none">{flag}</span> : null}
			{country}
		</span>
	);
}

function ChartPanelSkeleton() {
	return <div className="h-[27.375rem] animate-pulse bg-card" />;
}

function DetailSelect({
	onChange,
	options,
	renderValue,
	value,
}: {
	onChange: (value: string) => void;
	options: Array<{ label: string; value: string }>;
	renderValue?: (value: string) => React.ReactNode;
	value: string;
}) {
	const selectedLabel =
		options.find((option) => option.value === value)?.label ?? value;
	return (
		<Select value={value} onValueChange={onChange}>
			<SelectTrigger
				aria-label={`Revenue chart range: ${selectedLabel}`}
				className="h-9 min-w-[8rem] rounded-none border-0 border-b border-border bg-transparent px-2 py-1.5 font-mono text-sm uppercase tracking-[0.12em] text-foreground shadow-none hover:text-brand focus-visible:ring-0 [&_svg]:text-muted-foreground"
			>
				{renderValue ? (
					<div className="truncate">{renderValue(value)}</div>
				) : (
					<SelectValue />
				)}
			</SelectTrigger>
			<SelectContent className="rounded-none border-border bg-popover font-mono text-sm uppercase tracking-[0.12em] text-popover-foreground">
				{options.map((option) => (
					<SelectItem
						key={option.value}
						value={option.value}
						className="font-mono text-sm uppercase tracking-[0.12em] text-popover-foreground focus:bg-brand-soft focus:text-brand"
					>
						{option.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

function InlineChange({ value }: { value: number }) {
	if (value > 0) {
		return (
			<span className="font-mono text-sm tabular-nums text-[color:var(--ticker-up)]">
				▲ {formatPercent(value)}
			</span>
		);
	}

	if (value < 0) {
		return (
			<span className="font-mono text-sm tabular-nums text-[color:var(--ticker-down)]">
				▼ {formatPercent(value)}
			</span>
		);
	}

	return null;
}

function isPlaceholderText(text: string | null | undefined): boolean {
	if (!text) return true;
	const lower = text.toLowerCase();
	return lower.includes("lorem") || lower.includes("ipsum");
}

function FounderStory({ agent }: { agent: PublicAgentView }) {
	const rawMessage = agent.displayFounderMessage;
	const message =
		rawMessage && !isPlaceholderText(rawMessage) ? rawMessage.trim() : null;

	if (!message) return null;

	const paragraphs = message.split(/\n{2,}/).filter((p) => p.trim());

	return (
		<section className="mt-12 border border-border bg-card p-6 @2xl/page:p-8">
			<h2 className="font-mono text-sm uppercase tracking-[0.14em] text-foreground">
				Message from the founder
			</h2>
			<div className="mt-6 flex gap-4 @2xl/page:gap-5">
				<FounderAvatarCompact
					avatarUrl={agent.displayFounderAvatarUrl}
					founderName={agent.displayFounderName}
					size="lg"
				/>
				<div className="min-w-0 flex-1">
					<div className="space-y-4 text-pretty text-base leading-relaxed text-foreground">
						{paragraphs.map((paragraph) => (
							<p key={paragraph.slice(0, 24)}>{paragraph}</p>
						))}
					</div>
					<div className="mt-5">
						<p className="text-sm font-medium text-foreground">
							{agent.displayFounderName}
						</p>
						<p className="font-mono text-sm text-muted-foreground">
							Founder of {agent.displayName}
						</p>
					</div>
				</div>
			</div>
		</section>
	);
}

function AgentLivePulse() {
	return (
		<span className="relative inline-flex size-1.5" aria-hidden="true">
			<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[color:var(--ticker-up)] opacity-70" />
			<span className="relative inline-flex size-1.5 rounded-full bg-[color:var(--ticker-up)]" />
		</span>
	);
}

function RevenueSyncAttentionBanner({
	lastSyncedAt,
	provider,
}: {
	lastSyncedAt: Date | string;
	provider: string | null;
}) {
	const providerLabel = provider ? capitalize(provider) : "Revenue";
	return (
		<section className="mt-6 border-l-2 border-amber-500 bg-amber-500/10 px-4 py-3">
			<div className="flex items-start gap-3">
				<AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-500" />
				<div className="flex-1 space-y-1">
					<p className="font-mono text-sm uppercase tracking-[0.14em] text-amber-600 dark:text-amber-500">
						&gt; {providerLabel} sync needs attention
					</p>
					<p className="text-base leading-[1.55] text-foreground">
						Data last updated {formatBannerDate(lastSyncedAt)}.{" "}
						<Link
							to="/dashboard"
							className="font-mono text-brand transition-colors hover:text-brand-hover"
						>
							Update your API key
						</Link>{" "}
						or retry verification to sync latest revenue.
					</p>
				</div>
			</div>
		</section>
	);
}

function normalizeSnapshots(agent: AgentRecord) {
	const liveSnapshots = [...(agent.revenueSnapshots ?? [])]
		.map((snapshot) => ({
			...snapshot,
			// Keep the per-day count only when the snapshot actually has one.
			// Falling back to the current count fabricates a flat subscriber
			// history for providers that don't expose per-day data.
			activeSubscriptions:
				typeof snapshot.activeSubscriptions === "number"
					? snapshot.activeSubscriptions
					: undefined,
			chargeCount:
				typeof snapshot.chargeCount === "number"
					? snapshot.chargeCount
					: undefined,
			estimatedMrr:
				typeof snapshot.estimatedMrr === "number"
					? snapshot.estimatedMrr
					: agent.estimatedMrr,
			grossRevenue:
				typeof snapshot.grossRevenue === "number" ? snapshot.grossRevenue : 0,
		}))
		.sort(
			(left, right) =>
				new Date(left.snapshotDate).getTime() -
				new Date(right.snapshotDate).getTime(),
		);

	if (liveSnapshots.length > 0) {
		return normalizeRevenueTimeline(agent, liveSnapshots);
	}

	return buildSyntheticSnapshots(agent);
}

function normalizeRevenueTimeline(
	agent: AgentRecord,
	snapshots: SnapshotRecord[],
) {
	const totalRevenue = Math.max(agent.allTimeRevenue ?? 0, 0);
	if (totalRevenue <= 0) return snapshots;

	const dailyRevenueTotal = snapshots.reduce(
		(total, snapshot) => total + Math.max(snapshot.grossRevenue ?? 0, 0),
		0,
	);
	let runningRevenue = Math.max(totalRevenue - dailyRevenueTotal, 0);
	const cumulativeSnapshots = snapshots.map((snapshot, index) => {
		const dailyRevenue = Math.max(snapshot.grossRevenue ?? 0, 0);
		runningRevenue += dailyRevenue;
		return {
			...snapshot,
			grossRevenue:
				index === snapshots.length - 1
					? totalRevenue
					: Math.min(runningRevenue, totalRevenue),
		};
	});

	// Compare on UTC-day granularity: snapshotDate is normalized to midnight while
	// firstChargeDate keeps its intra-day time, so a same-day launch (first charge
	// at 23:10, first snapshot stamped 00:00) would wrongly read as "charge after
	// snapshot" and skip the zero anchor, leaving the chart pinned at the
	// backfilled lifetime lump instead of climbing from launch.
	const oneDayMs = 86_400_000;
	const firstChargeDay = agent.firstChargeDate
		? Math.floor(new Date(agent.firstChargeDate).getTime() / oneDayMs) *
			oneDayMs
		: Number.NaN;
	const firstSnapshotDay =
		Math.floor(
			new Date(cumulativeSnapshots[0]?.snapshotDate ?? Date.now()).getTime() /
				oneDayMs,
		) * oneDayMs;
	if (!Number.isFinite(firstChargeDay) || firstChargeDay > firstSnapshotDay) {
		return cumulativeSnapshots;
	}

	// Keep the anchor strictly before the first snapshot so the timeline stays in
	// chronological order: a same-day launch anchors at the day before instead of
	// colliding with the first real point.
	const anchorDate = new Date(
		Math.min(firstChargeDay, firstSnapshotDay - oneDayMs),
	).toISOString();
	return [
		{
			...cumulativeSnapshots[0],
			activeSubscriptions: 0,
			chargeCount: 0,
			createdAt: anchorDate,
			estimatedMrr: 0,
			grossRevenue: 0,
			id: `${agent.id}-first-charge-anchor`,
			snapshotDate: anchorDate,
			source: "derived",
		},
		...cumulativeSnapshots,
	];
}

function filterChartPoints(points: SnapshotRecord[], window: ChartWindow) {
	const zeroBaseline = buildZeroBaseline(points, window);
	if (zeroBaseline) {
		return zeroBaseline;
	}

	if (window === "all" || points.length <= 2) {
		return points;
	}

	const lastDate = new Date(
		points[points.length - 1]?.snapshotDate ?? Date.now(),
	);
	const startDate = new Date(lastDate);
	startDate.setDate(lastDate.getDate() - WINDOW_DAY_COUNT[window] + 1);

	const filtered = points.filter(
		(point) => new Date(point.snapshotDate).getTime() >= startDate.getTime(),
	);

	const result =
		filtered.length > 0 ? filtered : points.slice(-WINDOW_DAY_COUNT[window]);

	// If data doesn't reach the start of the window, prepend the last known
	// value before the window so the x-axis spans the range without inventing a
	// launch-from-zero jump.
	const firstPointDate = new Date(result[0]?.snapshotDate ?? lastDate);
	if (firstPointDate.getTime() > startDate.getTime()) {
		const previousPoint = points
			.filter(
				(point) => new Date(point.snapshotDate).getTime() < startDate.getTime(),
			)
			.at(-1);
		const anchorSource = previousPoint ?? result[0] ?? points[0];
		const anchor: SnapshotRecord = {
			...anchorSource,
			id: "anchor",
			snapshotDate: startDate.toISOString(),
			createdAt: startDate.toISOString(),
		};
		return [anchor, ...result];
	}

	return result;
}

function buildZeroBaseline(points: SnapshotRecord[], window: ChartWindow) {
	if (points.length !== 1) return null;
	const point = points[0];
	if (!point || point.grossRevenue > 0 || point.estimatedMrr > 0) return null;

	const endDate = new Date(point.snapshotDate);
	const startDate = new Date(endDate);
	if (window === "all") {
		startDate.setDate(endDate.getDate() - 1);
	} else {
		startDate.setDate(endDate.getDate() - WINDOW_DAY_COUNT[window] + 1);
	}

	return [
		{
			...point,
			id: `${point.id}-zero-baseline-start`,
			createdAt: startDate.toISOString(),
			snapshotDate: startDate.toISOString(),
		},
		{
			...point,
			id: `${point.id}-zero-baseline-end`,
			createdAt: endDate.toISOString(),
			snapshotDate: endDate.toISOString(),
		},
	];
}

function buildSyntheticSnapshots(agent: AgentRecord) {
	const totalDays = 365;
	const latestRevenue = Math.max(agent.estimatedMrr / 30, 0);
	const revenueTrend = (agent.momRevenueChangePct ?? 0) / 100;
	const mrrTrend = (agent.momMrrChangePct ?? 0) / 100;
	const baseDate = new Date();

	return Array.from({ length: totalDays }, (_, index) => {
		const date = new Date(baseDate);
		date.setDate(baseDate.getDate() - (totalDays - index - 1));

		const progress = index / Math.max(totalDays - 1, 1);
		const seasonal = 1 + Math.sin(index / 18) * 0.06;
		const revenue = Math.max(
			0,
			latestRevenue * (1 - revenueTrend * (1 - progress)) * seasonal,
		);
		const estimatedMrr = Math.max(
			0,
			agent.estimatedMrr * (1 - mrrTrend * (1 - progress)) * seasonal,
		);

		return {
			activeSubscriptions: Math.max(
				1,
				Math.round(agent.currentActiveSubscriptions * (0.8 + progress * 0.2)),
			),
			chargeCount: Math.max(
				1,
				Math.round(revenue / Math.max(latestRevenue, 1)),
			),
			createdAt: date.toISOString(),
			currency: "USD",
			estimatedMrr: Math.round(estimatedMrr),
			grossRevenue: Math.round(revenue),
			id: `${agent.slug}-snapshot-${index + 1}`,
			snapshotDate: date.toISOString(),
			source: "synthetic",
		};
	});
}

function buildDiscoverMoreAgents(
	allAgents: PublicAgentView[],
	currentAgent: PublicAgentView,
) {
	const ranked = [...allAgents]
		.filter((entry) => entry.slug !== currentAgent.slug)
		.sort((left, right) => {
			const unclaimedDelta =
				Number(left.isUnclaimed) - Number(right.isUnclaimed);
			if (unclaimedDelta !== 0) return unclaimedDelta;

			const currentCategories = new Set(
				coerceAgentCategories(currentAgent.categories),
			);
			const sharesCategory = (agent: typeof left) =>
				coerceAgentCategories(agent.categories).some((category) =>
					currentCategories.has(category),
				)
					? 1
					: 0;
			const categoryBias = sharesCategory(left) - sharesCategory(right);
			if (categoryBias !== 0) {
				return -categoryBias;
			}

			return comparePublicAgentRanking(left, right);
		});

	return ranked.slice(0, 6);
}

function calculatePercentChange(previous: number, next: number) {
	if (previous <= 0) {
		return next > 0 ? 100 : 0;
	}

	return ((next - previous) / previous) * 100;
}

function formatFounded(value: Date | string) {
	return new Intl.DateTimeFormat("en-US", {
		month: "long",
		year: "numeric",
	}).format(new Date(value));
}

function formatBannerDate(value: Date | string) {
	return new Intl.DateTimeFormat("en-US", {
		day: "numeric",
		month: "short",
		year: "numeric",
	}).format(new Date(value));
}

function formatVerifiedTimestamp(value: Date | string) {
	return new Intl.DateTimeFormat("en-US", {
		month: "long",
		day: "numeric",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: true,
	}).format(new Date(value));
}

function formatPercent(value: number) {
	return `${value > 0 ? "+" : ""}${new Intl.NumberFormat("en-US", {
		maximumFractionDigits: 0,
	}).format(value)}%`;
}

function capitalize(value: string) {
	return value.charAt(0).toUpperCase() + value.slice(1);
}

function countryToFlag(country: string) {
	const alpha2 = COUNTRY_CODES[country];
	if (!alpha2) return "";
	return alpha2
		.toUpperCase()
		.split("")
		.map((char) => String.fromCodePoint(char.charCodeAt(0) + 127397))
		.join("");
}

const COUNTRY_CODES: Record<string, string> = {
	Australia: "AU",
	Canada: "CA",
	France: "FR",
	Germany: "DE",
	Netherlands: "NL",
	Singapore: "SG",
	Spain: "ES",
	"United Kingdom": "GB",
	"United States": "US",
};
